import { useState, useEffect, useRef } from "react";
import { useLocalDraft } from "./useLocalDraft";
import { supabase } from "./supabaseclient";
import "./chartSetup";
import { Line, Bar, Doughnut } from "react-chartjs-2";

const T = {
  bg: "#06090f", panel: "#0b0f1a", card: "#0f1623", border: "#1a2540",
  gold: "#c9a96e", goldLight: "#e8c98a", goldDim: "#7a6640",
  text: "#e8eaf0", textMid: "#7a8599", textDim: "#3d4a5c",
  active: "#10b981", activeBg: "#071f14", activeText: "#34d399",
  completedBg: "#13132e", completedText: "#a5b4fc", moderate: "#fbbf24",
};

const CHART_DEFAULTS = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: "#0f1623",
      borderColor: "#1a2540",
      borderWidth: 1,
      titleColor: "#e8c98a",
      bodyColor: "#7a8599",
      padding: 12,
      cornerRadius: 8,
    },
  },
  scales: {
    x: {
      grid: { color: "#1a2540", drawBorder: false },
      ticks: { color: "#3d4a5c", font: { size: 11 } },
    },
    y: {
      grid: { color: "#1a2540", drawBorder: false },
      ticks: { color: "#3d4a5c", font: { size: 11 } },
    },
  },
};
const MRC_OPTIONS = [
  { value: "", label: "Select grade..." },
  { value: "0", label: "0 — No contraction" },
  { value: "1", label: "1 — Flicker/trace contraction" },
  { value: "2", label: "2 — Movement, gravity eliminated" },
  { value: "3", label: "3 — Movement against gravity" },
  { value: "4-", label: "4− — Against slight resistance" },
  { value: "4", label: "4 — Against moderate resistance" },
  { value: "4+", label: "4+ — Against strong resistance" },
  { value: "5", label: "5 — Normal strength" },
];

const PROGRESS_TRACK_OPTIONS = {
  shoulder: {
    rom: [
      "Shoulder Flexion",
      "Shoulder Abduction",
      "Shoulder External Rotation",
      "Shoulder Internal Rotation",
    ],
    mmt: [
      "Deltoid",
      "Supraspinatus",
      "Infraspinatus",
      "Subscapularis",
      "Serratus Anterior",
    ],
  },

  elbow: {
    rom: [
      "Elbow Flexion",
      "Elbow Extension",
      "Forearm Supination",
      "Forearm Pronation",
      "Wrist Extension",
      "Wrist Flexion",
      "Grip Strength",
    ],
    mmt: [
      "Biceps",
      "Triceps",
      "Wrist Extensors",
      "Wrist Flexors",
      "Grip Strength",
    ],
  },

  cervical: {
    rom: [
      "Cervical Flexion",
      "Cervical Extension",
      "Cervical Rotation Right",
      "Cervical Rotation Left",
      "Cervical Lateral Flexion Right",
      "Cervical Lateral Flexion Left",
    ],
    mmt: [
      "Deep Neck Flexors",
      "Upper Trapezius",
      "Sternocleidomastoid",
      "Lower Trapezius",
    ],
  },

  lumbar: {
    rom: [
      "Lumbar Flexion",
      "Lumbar Extension",
      "Lumbar Lateral Flexion Right",
      "Lumbar Lateral Flexion Left",
      "SLR Angle",
    ],
    mmt: [
      "Transversus Abdominis",
      "Multifidus",
      "Hip Flexors",
      "Gluteus Maximus",
    ],
  },

  hip: {
    rom: [
      "Hip Flexion",
      "Hip Extension",
      "Hip Abduction",
      "Hip Adduction",
      "Hip External Rotation",
      "Hip Internal Rotation",
    ],
    mmt: [
      "Gluteus Maximus",
      "Gluteus Medius",
      "Hip Flexors",
      "Adductors",
      "Hip External Rotators",
    ],
  },

  knee: {
    rom: [
      "Knee Flexion",
      "Knee Extension",
    ],
    mmt: [
      "Quadriceps",
      "Hamstrings",
      "Gluteus Medius",
      "Gluteus Maximus",
    ],
  },

  ankle: {
    rom: [
      "Ankle Dorsiflexion",
      "Ankle Plantarflexion",
      "Ankle Inversion",
      "Ankle Eversion",
    ],
    mmt: [
      "Gastrocnemius/Soleus",
      "Tibialis Anterior",
      "Peroneals",
      "Intrinsic Foot Muscles",
    ],
  },
};

function FieldLabel({ children }) {
  return (
    <label style={{ display: "block", fontSize: 11, fontWeight: 600, letterSpacing: "0.7px", textTransform: "uppercase", color: T.textDim, marginBottom: 5 }}>
      {children}
    </label>
  );
}

function SectionLabel({ text }) {
  return (
    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.9px", textTransform: "uppercase", color: T.goldDim, marginBottom: 13 }}>
      {text}
    </div>
  );
}

function StatCard({ label, value, sub, color, icon }) {
  return (
    <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, padding: "20px 22px", position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${color}, ${color}88)` }} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <div style={{ fontSize: 10, color: T.textDim, fontWeight: 700, letterSpacing: "0.8px", textTransform: "uppercase", marginBottom: 10 }}>{label}</div>
          <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 34, fontWeight: 700, color, lineHeight: 1 }}>{value}</div>
          <div style={{ fontSize: 11, color: T.textDim, marginTop: 6 }}>{sub}</div>
        </div>
        <div style={{ fontSize: 28, opacity: 0.15, color }}>{icon}</div>
      </div>
    </div>
  );
}

export default function ProgressAnalytics({ patient, session, notify, effectiveJoint }) {
  const [progressData, setProgressData] = useState([]);
  const [outcomeData, setOutcomeData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeChart, setActiveChart] = useState("pain");
const [form, setForm, clearForm] = useLocalDraft(`progress_form_${patient.id}`, {
  session_number: "",
  pain_score: "",
  rom_type: "",
  rom_score: "",
  strength_type: "",
  strength_score: "",
  notes: "",
});

const jointKey = effectiveJoint || "lumbar";
const progressConfig = PROGRESS_TRACK_OPTIONS[jointKey] || PROGRESS_TRACK_OPTIONS.lumbar;

  useEffect(() => {
    if (patient?.id) { fetchProgress(); fetchOutcomes(); }
  }, [patient]);

  async function fetchProgress() {
    setLoading(true);
    const { data, error } = await supabase
      .from("session_progress")
      .select("*")
      .eq("patient_id", patient.id)
      .eq("user_id", session.user.id)
      .order("session_number", { ascending: true });
    if (!error) setProgressData(data || []);
    setLoading(false);
  }

  async function fetchOutcomes() {
    const { data } = await supabase
      .from("outcome_measures")
      .select("*")
      .eq("patient_id", patient.id)
      .eq("user_id", session.user.id)
      .order("created_at", { ascending: true });
    setOutcomeData(data || []);
  }

  async function saveProgress() {
    if (!form.session_number) { notify("Session number is required", "error"); return; }
    setSaving(true);
    const { error } = await supabase.from("session_progress").insert([{
  patient_id: patient.id,
  user_id: session.user.id,
  session_number: Number(form.session_number),
  pain_score: form.pain_score !== "" ? Number(form.pain_score) : null,
  rom_type: form.rom_type || null,
  rom_score: form.rom_score !== "" ? Number(form.rom_score) : null,
  strength_type: form.strength_type || null,
  strength_score: form.strength_score !== "" ? Number(form.strength_score) : null,
  notes: form.notes || null,
}]);
    if (error) { notify("Save failed: " + error.message, "error"); setSaving(false); return; }
    notify("Progress recorded ✓");
    clearForm();
    setSaving(false);
    setShowForm(false);
    setForm({
  session_number: "",
  pain_score: "",
  rom_type: "",
  rom_score: "",
  strength_type: "",
  strength_score: "",
  notes: "",
});
    fetchProgress();
  }

  // ── Chart data ──
  const labels = progressData.map((d) => `S${d.session_number}`);

  const painData = {
    labels,
    datasets: [{
      label: "Pain Score",
      data: progressData.map((d) => d.pain_score),
      borderColor: "#f87171",
      backgroundColor: "rgba(248,113,113,0.08)",
      pointBackgroundColor: "#f87171",
      pointBorderColor: T.card,
      pointBorderWidth: 2,
      pointRadius: 5,
      fill: true,
      tension: 0.4,
    }],
  };

  const romData = {
    labels,
    datasets: [{
      label: "ROM (°)",
      data: progressData.map((d) => d.rom_score),
      borderColor: T.activeText,
      backgroundColor: "rgba(52,211,153,0.08)",
      pointBackgroundColor: T.activeText,
      pointBorderColor: T.card,
      pointBorderWidth: 2,
      pointRadius: 5,
      fill: true,
      tension: 0.4,
    }],
  };

  const strengthData = {
    labels,
    datasets: [{
      label: "Strength (MRC)",
      data: progressData.map((d) => d.strength_score),
      backgroundColor: progressData.map((d) =>
        d.strength_score >= 4 ? "rgba(52,211,153,0.7)" :
        d.strength_score >= 3 ? "rgba(251,191,36,0.7)" :
        "rgba(248,113,113,0.7)"
      ),
      borderColor: progressData.map((d) =>
        d.strength_score >= 4 ? T.activeText :
        d.strength_score >= 3 ? T.moderate :
        "#f87171"
      ),
      borderWidth: 1,
      borderRadius: 6,
    }],
  };

  // Outcome measures grouped by scale
  const outcomeByScale = outcomeData.reduce((acc, e) => {
    if (!acc[e.scale_name]) acc[e.scale_name] = [];
    acc[e.scale_name].push(e);
    return acc;
  }, {});

  const SCALE_COLORS = {
    VAS: "#f87171", OSWESTRY: "#fbbf24", DASH: "#a78bfa",
    LEFS: "#34d399", WOMAC: "#22d3ee", BERG: "#c9a96e",
  };

  const SCALE_NAMES = {
    VAS: "VAS", OSWESTRY: "Oswestry", DASH: "DASH",
    LEFS: "LEFS", WOMAC: "WOMAC", BERG: "Berg Balance",
  };

  // Summary stats
  const painEntries = progressData.filter((d) => d.pain_score !== null);
  const firstPain = painEntries[0]?.pain_score;
  const lastPain = painEntries[painEntries.length - 1]?.pain_score;
  const painReduction = firstPain !== undefined && lastPain !== undefined ? +(firstPain - lastPain).toFixed(1) : null;
  const sessionPct = Math.min(Math.round((patient.sessions / Math.max(patient.totalSessions, 1)) * 100), 100);

  // Recovery status
  function getRecoveryStatus() {
    if (painReduction === null) return { label: "No Data", color: T.textDim };
    if (painReduction > 3) return { label: "Good Progress", color: T.activeText };
    if (painReduction > 0) return { label: "Improving", color: T.moderate };
    if (painReduction === 0) return { label: "Plateau", color: T.moderate };
    return { label: "Worsening", color: "#f87171" };
  }
  const recovery = getRecoveryStatus();

  const chartOptions = (yLabel, min, max) => ({
    ...CHART_DEFAULTS,
    plugins: { ...CHART_DEFAULTS.plugins },
    scales: {
      x: { ...CHART_DEFAULTS.scales.x },
      y: {
        ...CHART_DEFAULTS.scales.y,
        min,
        max,
        title: { display: true, text: yLabel, color: T.textDim, font: { size: 10 } },
      },
    },
  });

  return (
    <div>
      {/* ── SUMMARY CARDS ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 14, marginBottom: 28 }}>
        <StatCard label="Sessions Completed" value={`${patient.sessions}/${patient.totalSessions}`} sub={`${sessionPct}% of treatment plan`} color={T.gold} icon="◷" />
        <StatCard label="Current Pain (NPRS)" value={`${patient.painScore}/10`} sub={patient.painScore <= 3 ? "Mild pain" : patient.painScore <= 6 ? "Moderate pain" : "Severe pain"} color={patient.painScore <= 3 ? T.activeText : patient.painScore <= 6 ? T.moderate : "#f87171"} icon="◉" />
        <StatCard
          label="Pain Reduction"
          value={painReduction !== null ? (painReduction > 0 ? `↓ ${painReduction}` : painReduction < 0 ? `↑ ${Math.abs(painReduction)}` : "→ 0") : "—"}
          sub={recovery.label}
          color={recovery.color}
          icon="▼"
        />
        <StatCard label="Outcome Assessments" value={outcomeData.length} sub={`${Object.keys(outcomeByScale).length} scales completed`} color="#a78bfa" icon="◈" />
      </div>

      {/* ── RECORD PROGRESS ── */}
      <div
  style={{
    marginBottom: 16,
    fontSize: 13,
    color: T.textMid,
  }}
>
  Tracking joint / region:
  <span
    style={{
      color: T.goldLight,
      fontWeight: 700,
      marginLeft: 8,
    }}
  >
    {effectiveJoint || "Not selected"}
  </span>
</div>
      <div style={{ marginBottom: 28 }}>
        {!showForm ? (
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <button
              className="btn btn-gold"
             onClick={() => {
  setShowForm(true);
  setForm({
    session_number: String((patient?.sessions || 0) + 1),
    pain_score: "",
    rom_type: progressConfig.rom[0] || "",
    rom_score: "",
    strength_type: progressConfig.mmt[0] || "",
    strength_score: "",
    notes: "",
  });
}}
              style={{ display: "flex", alignItems: "center", gap: 8 }}
            >
              <span style={{ fontSize: 18 }}>+</span> Record Session Progress
            </button>
            {progressData.length > 0 && (
              <div style={{ fontSize: 12, color: T.textDim }}>
                Last recorded: Session #{progressData[progressData.length - 1]?.session_number} · {new Date(progressData[progressData.length - 1]?.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
              </div>
            )}
          </div>
        ) : (
          <div className="card" style={{ padding: 28 }}>
            <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 20, fontWeight: 600, color: T.goldLight, marginBottom: 20 }}>Record Session Progress</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 16, marginBottom: 16 }}>
              <div>
                <FieldLabel>Session Number *</FieldLabel>
                <input type="number" value={form.session_number} onChange={(e) => setForm((f) => ({ ...f, session_number: e.target.value }))} placeholder="e.g. 5" />
              </div>
              <div>
                <FieldLabel>Pain Score (NPRS 0–10)</FieldLabel>
                <select value={form.pain_score} onChange={(e) => setForm((f) => ({ ...f, pain_score: e.target.value }))}>
                  <option value="">Select...</option>
                  {[0,1,2,3,4,5,6,7,8,9,10].map((n) => <option key={n} value={n}>{n} — {n === 0 ? "No pain" : n <= 3 ? "Mild" : n <= 6 ? "Moderate" : "Severe"}</option>)}
                </select>
              </div>
              <div>
  <FieldLabel>Primary ROM to Track</FieldLabel>
  <select
    value={form.rom_type}
    onChange={(e) =>
      setForm((f) => ({ ...f, rom_type: e.target.value }))
    }
  >
    <option value="">Select ROM...</option>
    {progressConfig.rom.map((item) => (
      <option key={item} value={item}>
        {item}
      </option>
    ))}
  </select>
</div>

<div>
  <FieldLabel>ROM Value (degrees)</FieldLabel>
  <input
    type="number"
    value={form.rom_score}
    onChange={(e) =>
      setForm((f) => ({ ...f, rom_score: e.target.value }))
    }
    placeholder="Enter ROM value"
    min={0}
    max={180}
  />
</div>

<div>
  <FieldLabel>Primary MMT to Track</FieldLabel>
  <select
    value={form.strength_type}
    onChange={(e) =>
      setForm((f) => ({ ...f, strength_type: e.target.value }))
    }
  >
    <option value="">Select muscle...</option>
    {progressConfig.mmt.map((item) => (
      <option key={item} value={item}>
        {item}
      </option>
    ))}
  </select>
</div>

<div>
  <FieldLabel>Strength Grade (MRC 0–5)</FieldLabel>
  <select
    value={form.strength_score}
    onChange={(e) =>
      setForm((f) => ({ ...f, strength_score: e.target.value }))
    }
  >
    {MRC_OPTIONS.map((o) => (
      <option key={o.value} value={o.value}>
        {o.label}
      </option>
    ))}
  </select>
</div>
              <div style={{ gridColumn: "1 / -1" }}>
                <FieldLabel>Session Notes</FieldLabel>
                <textarea rows={3} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} placeholder="Key clinical observations, patient feedback, home program compliance..." style={{ resize: "vertical" }} />
              </div>
            </div>
            <div style={{ display: "flex", gap: 12 }}>
              <button className="btn btn-gold" onClick={saveProgress} disabled={saving}>{saving ? "Saving..." : "Save Progress"}</button>
              <button className="btn btn-outline" onClick={() => setShowForm(false)}>Cancel</button>
            </div>
          </div>
        )}
      </div>

      {/* ── CHARTS ── */}
      {progressData.length > 0 ? (
        <>
          {/* Chart Selector */}
          <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
            {[
              { id: "pain", label: "Pain Trend", color: "#f87171" },
              { id: "rom", label: "ROM Progress", color: T.activeText },
              { id: "strength", label: "Strength", color: "#a78bfa" },
            ].map((c) => (
              <button
                key={c.id}
                onClick={() => setActiveChart(c.id)}
                className="btn"
                style={{
                  padding: "8px 18px",
                  fontSize: 12,
                  fontWeight: 600,
                  background: activeChart === c.id ? `${c.color}18` : "transparent",
                  border: `1px solid ${activeChart === c.id ? c.color : T.border}`,
                  color: activeChart === c.id ? c.color : T.textMid,
                  borderRadius: 8,
                }}
              >
                {c.label}
              </button>
            ))}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 20, marginBottom: 20 }}>
            {/* Main Chart */}
            <div className="card" style={{ padding: 24 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                <div>
                  <SectionLabel text={activeChart === "pain"
  ? "Pain Score Trend (NPRS)"
  : activeChart === "rom"
  ? `ROM Progress${progressData.find((d) => d.rom_type)?.rom_type ? ` — ${progressData.find((d) => d.rom_type)?.rom_type}` : ""}`
  : `Muscle Strength${progressData.find((d) => d.strength_type)?.strength_type ? ` — ${progressData.find((d) => d.strength_type)?.strength_type}` : ""}`} />
                  <div style={{ fontSize: 11, color: T.textDim, marginTop: -8 }}>
                    {activeChart === "pain" ? "Lower scores indicate improvement" : activeChart === "rom" ? "Higher degrees indicate improvement" : "Higher grade indicates improvement"}
                  </div>
                </div>
                {activeChart === "pain" && painReduction !== null && (
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 11, color: T.textDim }}>Total change</div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: painReduction > 0 ? T.activeText : painReduction < 0 ? "#f87171" : T.moderate }}>
                      {painReduction > 0 ? `↓ ${painReduction}` : painReduction < 0 ? `↑ ${Math.abs(painReduction)}` : "→ 0"}
                    </div>
                  </div>
                )}
              </div>
              <div style={{ height: 260 }}>
                {activeChart === "pain" && (
                  <Line data={painData} options={chartOptions("NPRS Score", 0, 10)} />
                )}
                {activeChart === "rom" && (
                  progressData.some(d => d.rom_score !== null)
                    ? <Line data={romData} options={chartOptions("Degrees (°)", 0, 180)} />
                    : <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: T.textDim, fontSize: 13 }}>No ROM data recorded yet</div>
                )}
                {activeChart === "strength" && (
                  progressData.some(d => d.strength_score !== null)
                    ? <Bar data={strengthData} options={chartOptions("MRC Grade", 0, 5)} />
                    : <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: T.textDim, fontSize: 13 }}>No strength data recorded yet</div>
                )}
              </div>
            </div>

            {/* Session Completion Doughnut */}
            <div className="card" style={{ padding: 24, display: "flex", flexDirection: "column" }}>
              <SectionLabel text="Treatment Completion" />
              <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                <div style={{ width: 160, height: 160, position: "relative" }}>
                  <Doughnut
                    data={{
                      datasets: [{
                        data: [patient.sessions, Math.max(patient.totalSessions - patient.sessions, 0)],
                        backgroundColor: [`${T.gold}cc`, T.border],
                        borderWidth: 0,
                        circumference: 270,
                        rotation: 225,
                      }],
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      cutout: "75%",
                      plugins: { legend: { display: false }, tooltip: { enabled: false } },
                    }}
                  />
                  <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                    <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 32, fontWeight: 700, color: T.gold }}>{sessionPct}%</div>
                    <div style={{ fontSize: 10, color: T.textDim, textTransform: "uppercase", letterSpacing: "0.6px" }}>Complete</div>
                  </div>
                </div>
                <div style={{ marginTop: 16, textAlign: "center" }}>
                  <div style={{ fontSize: 13, color: T.text, fontWeight: 600 }}>{patient.sessions} of {patient.totalSessions} sessions</div>
                  <div style={{ fontSize: 11, color: T.textDim, marginTop: 4 }}>{Math.max(patient.totalSessions - patient.sessions, 0)} sessions remaining</div>
                </div>

                {/* Recovery status pill */}
                <div style={{ marginTop: 16, padding: "6px 16px", borderRadius: 20, background: `${recovery.color}18`, border: `1px solid ${recovery.color}44`, fontSize: 12, fontWeight: 700, color: recovery.color }}>
                  {recovery.label}
                </div>
              </div>
            </div>
          </div>

          {/* Outcome Measures Trend Charts */}
          {Object.keys(outcomeByScale).length > 0 && (
            <div className="card" style={{ padding: 24, marginBottom: 20 }}>
              <SectionLabel text="Outcome Measures Trend" />
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 20 }}>
                {Object.entries(outcomeByScale).map(([scale, entries]) => {
                  if (entries.length < 1) return null;
                  const color = SCALE_COLORS[scale] || T.gold;
                  const isLowerBetter = ["VAS","OSWESTRY","DASH","WOMAC"].includes(scale);
                  const chartData = {
                    labels: entries.map((_, i) => `#${i + 1}`),
                    datasets: [{
                      label: SCALE_NAMES[scale],
                      data: entries.map((e) => e.score),
                      borderColor: color,
                      backgroundColor: `${color}12`,
                      pointBackgroundColor: color,
                      pointBorderColor: T.card,
                      pointBorderWidth: 2,
                      pointRadius: 5,
                      fill: true,
                      tension: 0.4,
                    }],
                  };
                  const first = entries[0]?.score;
                  const last = entries[entries.length - 1]?.score;
                  const diff = last - first;
                  const improved = isLowerBetter ? diff < 0 : diff > 0;
                  return (
                    <div key={scale}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color }}>{SCALE_NAMES[scale]}</div>
                        {entries.length > 1 && (
                          <div style={{ fontSize: 11, fontWeight: 700, color: improved ? T.activeText : "#f87171" }}>
                            {improved ? "↑ Improving" : "↓ Declining"}
                          </div>
                        )}
                      </div>
                      <div style={{ height: 100 }}>
                        <Line data={chartData} options={{
                          ...CHART_DEFAULTS,
                          scales: {
                            x: { ...CHART_DEFAULTS.scales.x },
                            y: { ...CHART_DEFAULTS.scales.y, min: 0, max: entries[0]?.max_score },
                          },
                        }} />
                      </div>
                      <div style={{ fontSize: 10, color: T.textDim, marginTop: 4, textAlign: "center" }}>
                        {isLowerBetter ? "Lower is better" : "Higher is better"} · Max {entries[0]?.max_score}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="card" style={{ padding: 48, textAlign: "center", marginBottom: 20 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>📊</div>
          <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 22, color: T.goldLight, marginBottom: 8 }}>No Progress Data Yet</div>
          <div style={{ fontSize: 13, color: T.textDim, marginBottom: 20 }}>Record session progress after each treatment to visualize recovery charts, pain trends, and ROM improvements.</div>
          <button className="btn btn-gold" onClick={() => { setShowForm(true); setForm({ session_number: "1",
  pain_score: "",
  rom_type: "",
  rom_score: "",
  strength_type: "",
  strength_score: "",
  notes: "", }); }}>
            + Record First Session
          </button>
        </div>
      )}

      {/* ── SESSION LOG TABLE ── */}
      {progressData.length > 0 && (
        <div className="card" style={{ padding: 24 }}>
          <SectionLabel text="Session-by-Session Clinical Log" />
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: T.panel }}>
                  {["Session", "Pain Score", "ROM", "Strength (MRC)", "Clinical Notes", "Date"].map((h) => (
                    <th key={h} style={{ padding: "12px 16px", textAlign: "left", fontSize: 9, fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase", color: T.textDim, borderBottom: `1px solid ${T.border}`, whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[...progressData].reverse().map((row, i) => (
                  <tr key={row.id} style={{ borderBottom: `1px solid ${T.border}`, background: i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.01)" }}>
                    <td style={{ padding: "14px 16px" }}>
                      <div style={{ width: 32, height: 32, borderRadius: "50%", background: `${T.gold}18`, border: `1px solid ${T.gold}44`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: T.gold }}>
                        {row.session_number}
                      </div>
                    </td>
                    <td style={{ padding: "14px 16px" }}>
                      {row.pain_score !== null ? (
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <div style={{ width: 60, height: 6, borderRadius: 3, background: T.border, overflow: "hidden" }}>
                            <div style={{ height: "100%", borderRadius: 3, width: `${(row.pain_score / 10) * 100}%`, background: row.pain_score <= 3 ? T.activeText : row.pain_score <= 6 ? T.moderate : "#f87171" }} />
                          </div>
                          <span style={{ fontWeight: 700, color: row.pain_score <= 3 ? T.activeText : row.pain_score <= 6 ? T.moderate : "#f87171", fontSize: 13 }}>{row.pain_score}/10</span>
                        </div>
                      ) : <span style={{ color: T.textDim }}>—</span>}
                    </td>
                    <td style={{ padding: "14px 16px", color: T.textMid, fontWeight: 600 }}>
  {row.rom_score !== null ? (
    <>
      {row.rom_type ? `${row.rom_type}: ` : ""}
      {row.rom_score}°
    </>
  ) : (
    <span style={{ color: T.textDim }}>—</span>
  )}
</td>
                    <td style={{ padding: "14px 16px" }}>
  {row.strength_score !== null ? (
    <span
      style={{
        padding: "3px 10px",
        borderRadius: 12,
        fontSize: 11,
        fontWeight: 700,
        background:
          row.strength_score >= 4
            ? "rgba(52,211,153,0.1)"
            : row.strength_score >= 3
            ? "rgba(251,191,36,0.1)"
            : "rgba(248,113,113,0.1)",
        color:
          row.strength_score >= 4
            ? T.activeText
            : row.strength_score >= 3
            ? T.moderate
            : "#f87171",
      }}
    >
      {row.strength_type ? `${row.strength_type}: ` : ""}
      {row.strength_score}/5
    </span>
  ) : (
    <span style={{ color: T.textDim }}>—</span>
  )}
</td>
                    <td style={{ padding: "14px 16px", color: T.textDim, maxWidth: 240, fontSize: 12 }}>{row.notes || <span style={{ fontStyle: "italic" }}>No notes</span>}</td>
                    <td style={{ padding: "14px 16px", color: T.textDim, whiteSpace: "nowrap", fontSize: 12 }}>{new Date(row.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}